import { Chat, type StateErrorPayload } from "@launch10/use-langgraph-stream";
import { csrfFetch } from "@lib/csrfFetch";
import { toast } from "sonner";

/**
 * Auto-file a support ticket on the user's behalf when they click
 * "send the error to our Support Team" in the chat error banner. We
 * post the same shape `SupportController#create` accepts from
 * `ContactForm` — Inertia would redirect on success, which we don't
 * want inside a chat surface, so we use `csrfFetch` and skip the
 * navigation. The server-side notification side-effects
 * (Notion / Slack workers, support mailer) fire in `SupportRequest`
 * callbacks regardless of how the request arrived.
 *
 * Because we bypass Inertia, the Rails flash never reaches the page
 * props and `useFlashMessages` doesn't fire — so the success toast
 * that ContactForm gets "for free" is missing here. Match that UX
 * by firing the same `toast.success(...)` directly. Title/copy
 * mirrors the controller's `redirect_to support_path, notice:` shape.
 */
async function fileSupportTicket(error: StateErrorPayload): Promise<void> {
  const formData = new FormData();
  formData.append("support_request[category]", "Report a bug");
  formData.append(
    "support_request[subject]",
    `Error in chat ${window.location.href}`
  );
  formData.append(
    "support_request[description]",
    [
      "Automated bug report filed from the in-chat error banner.",
      "",
      `URL: ${window.location.href}`,
      `Node: ${error.node}`,
      `Kind: ${error.kind}`,
      `Message: ${error.message}`,
    ].join("\n")
  );
  formData.append("support_request[submitted_from_url]", window.location.href);
  formData.append("support_request[browser_info]", navigator.userAgent);

  const response = await csrfFetch("/support", {
    method: "POST",
    body: formData,
  });
  if (!response.ok && response.status >= 400 && response.status < 500) {
    // 422 = validation error. Anything else 4xx is also a hard "won't
    // succeed on retry" — surface as failure so the banner shows the
    // "couldn't send" affordance.
    throw new Error(`Support submit failed: ${response.status}`);
  }

  toast.success("Request submitted", {
    description: "We'll get back to you within 24 hours.",
    duration: 5000,
  });
}

/**
 * Chat error banner pre-wired with one-click bug report. Used everywhere
 * a Chat surface is rendered (website, brainstorm, ads, support). The
 * banner itself lives in `@launch10/use-langgraph-stream` — this wrapper
 * just supplies the host-side support submission.
 */
export function ChatErrorBanner() {
  return <Chat.ErrorBanner onReportToSupport={fileSupportTicket} />;
}
